import React, { useEffect, useState, useContext } from 'react';
import { Button } from './ui/Button';
import { NavigationContext } from '../App';
import { ChevronLeft, ChevronRight } from 'lucide-react';
// Product images for the slider
const IMAGES = {
  blackGoldDress: 'https://cdn.magicpatterns.com/uploads/uv6odkMa2mkp5WJJsrmoHR/hero_5-artguru.png',
  burgundyDress: 'https://cdn.magicpatterns.com/uploads/w4uDyTUcYkbK4NzfnYbtMo/hero_6-artguru.png',
  purplePeplumSet: 'https://cdn.magicpatterns.com/uploads/phWGog7DVYqvsh6NBfZegH/hero_1.jpg',
  greenDress: 'https://cdn.magicpatterns.com/uploads/hwwBxqVkpV4c2h7vPnQnxH/hero_3-artguru.png',
  redRuffleDress: 'https://cdn.magicpatterns.com/uploads/1DuHNVR2Z2JmBtQfHjokgf/hero_4.jpg'
};
const slides = [{
  id: 1,
  image: IMAGES.blackGoldDress,
  alt: 'Black & Gold Polka Dot Midi Dress'
}, {
  id: 2,
  image: IMAGES.burgundyDress,
  alt: 'Burgundy Pleated Dress with Embroidered Cape'
}, {
  id: 3,
  image: IMAGES.purplePeplumSet,
  alt: 'Purple Ruffle Peplum Set with Embellishments'
}, {
  id: 4,
  image: IMAGES.greenDress,
  alt: 'Olive Green Crystal Embellished Midi Dress'
}, {
  id: 5,
  image: IMAGES.redRuffleDress,
  alt: 'Red Ruffle Pleated Dress with Gold Accents'
}];
export function HeroBanner() {
  const {
    navigate
  } = useContext(NavigationContext);
  const [currentSlide, setCurrentSlide] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);
  const goToSlide = (index: number) => setCurrentSlide(index);
  const goToPrev = () => setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length);
  const goToNext = () => setCurrentSlide(prev => (prev + 1) % slides.length);
  return <section className="relative w-full min-h-[60vh] sm:min-h-[70vh] lg:min-h-[85vh] overflow-hidden bg-[#5C3D2E]" data-id="element-175">
      {/* Kente-inspired top accent */}
      <div className="absolute top-0 left-0 right-0 h-1.5 kente-stripe z-20" data-id="element-176" />

      {/* Fashion-inspired Pattern Overlays */}
      {/* Layer 1: Dress form / mannequin silhouettes */}
      <div className="absolute inset-0 fashion-mannequin-pattern" data-id="element-177" />

      {/* Layer 2: Delicate florals */}
      <div className="absolute inset-0 fashion-scissors-pattern" data-id="element-178" />

      {/* Layer 3: Pearl string pattern */}
      <div className="absolute inset-0 fashion-thread-pattern" data-id="element-179" />

      {/* Layer 4: Ribbon bows */}
      <div className="absolute inset-0 fashion-swatch-pattern" data-id="element-180" />

      {/* Layer 5: Fashion sketch lines */}
      <div className="absolute inset-0 fashion-sketch-pattern" data-id="element-181" />

      {/* Layer 6: Lace-inspired pattern */}
      <div className="absolute inset-0 fashion-buttons-pattern" data-id="element-182" />

      {/* Decorative feminine elements - pearls, ribbons, soft curves */}
      {/* Pearl necklace curve - top left */}
      <div className="absolute top-16 left-8 sm:left-16" data-id="element-183">
        <div className="w-4 h-4 rounded-full bg-[#E8C97A]/40 absolute shadow-sm" data-id="element-184" />
        <div className="w-3.5 h-3.5 rounded-full bg-[#E8C97A]/35 absolute top-6 left-5" data-id="element-185" />
        <div className="w-3 h-3 rounded-full bg-[#D4A853]/35 absolute top-11 left-9" data-id="element-186" />
        <div className="w-3.5 h-3.5 rounded-full bg-[#E8C97A]/30 absolute top-16 left-12" data-id="element-187" />
        <div className="w-3 h-3 rounded-full bg-[#D4A853]/35 absolute top-[84px] left-14" data-id="element-188" />
        <div className="w-2.5 h-2.5 rounded-full bg-[#E8C97A]/30 absolute top-[104px] left-15" data-id="element-189" />
      </div>

      {/* Elegant ribbon swirl - top right */}
      <div className="absolute top-24 right-12 sm:right-24 w-24 h-24 sm:w-36 sm:h-36" data-id="element-190">
        <div className="absolute inset-0 border-t-[3px] border-r-[3px] border-[#E8C97A]/35 rounded-tr-full" data-id="element-191" />
        <div className="absolute top-3 right-3 w-20 h-20 sm:w-30 sm:h-30 border-t-2 border-r-2 border-[#D4A853]/30 rounded-tr-full" data-id="element-192" />
        <div className="absolute top-6 right-6 w-14 h-14 sm:w-22 sm:h-22 border-t border-r border-[#E8C97A]/25 rounded-tr-full" data-id="element-193" />
      </div>

      {/* Soft bow shape - right side */}
      <div className="absolute top-1/3 right-8 sm:right-20" data-id="element-194">
        <div className="w-16 h-8 sm:w-24 sm:h-12 border-2 border-[#E8C97A]/35 rounded-full transform -rotate-12" data-id="element-195" />
        <div className="w-16 h-8 sm:w-24 sm:h-12 border-2 border-[#D4A853]/30 rounded-full transform rotate-12 -mt-4 ml-3" data-id="element-196" />
        <div className="w-5 h-5 sm:w-7 sm:h-7 bg-[#E8C97A]/30 rounded-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 shadow-sm" data-id="element-197" />
      </div>

      {/* Flowing fabric curves - bottom left */}
      <div className="absolute bottom-24 left-4 sm:left-12" data-id="element-198">
        <div className="w-40 h-20 sm:w-52 sm:h-28 border-b-[3px] border-l-2 border-[#E8C97A]/30 rounded-bl-[80px]" data-id="element-199" />
        <div className="w-32 h-16 sm:w-44 sm:h-22 border-b-2 border-l border-[#D4A853]/25 rounded-bl-[60px] absolute top-3 left-5" data-id="element-200" />
        <div className="w-24 h-12 sm:w-36 sm:h-16 border-b border-l border-[#E8C97A]/20 rounded-bl-[50px] absolute top-6 left-10" data-id="element-201" />
      </div>

      {/* Pearl drops - scattered */}
      <div className="absolute top-1/2 right-4 sm:right-10 flex flex-col gap-4" data-id="element-202">
        <div className="w-4 h-4 rounded-full bg-[#E8C97A]/35 shadow-sm" data-id="element-203" />
        <div className="w-3 h-3 rounded-full bg-[#D4A853]/40 ml-3" data-id="element-204" />
        <div className="w-5 h-5 rounded-full bg-[#E8C97A]/30 shadow-sm" data-id="element-205" />
        <div className="w-3 h-3 rounded-full bg-[#D4A853]/35 ml-2" data-id="element-206" />
        <div className="w-4 h-4 rounded-full bg-[#E8C97A]/30" data-id="element-207" />
      </div>

      {/* Delicate lace-like circles - bottom right */}
      <div className="absolute bottom-28 right-16 sm:right-32" data-id="element-208">
        <div className="w-20 h-20 sm:w-28 sm:h-28 rounded-full border-2 border-[#E8C97A]/35 border-dashed" data-id="element-209" />
        <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-full border-2 border-[#D4A853]/30 absolute top-3 left-3" data-id="element-210" />
        <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-full border border-[#E8C97A]/35 absolute top-6 left-6" data-id="element-211" />
        <div className="w-4 h-4 sm:w-6 sm:h-6 rounded-full bg-[#D4A853]/25 absolute top-8 left-8" data-id="element-212" />
      </div>

      {/* Elegant vertical accent lines */}
      <div className="absolute top-1/4 left-2 sm:left-6 h-32 w-0.5 bg-gradient-to-b from-transparent via-[#E8C97A]/30 to-transparent" data-id="element-213" />
      <div className="absolute top-1/3 left-5 sm:left-12 h-24 w-0.5 bg-gradient-to-b from-transparent via-[#D4A853]/25 to-transparent" data-id="element-214" />
      <div className="absolute top-[45%] left-3 sm:left-8 h-20 w-0.5 bg-gradient-to-b from-transparent via-[#E8C97A]/20 to-transparent" data-id="element-215" />

      {/* Mobile-only feminine elements */}
      <div className="lg:hidden absolute top-1/2 right-2 -translate-y-1/2" data-id="element-216">
        <div className="w-32 h-32 rounded-full border-2 border-[#E8C97A]/30 border-dashed" data-id="element-217" />
        <div className="w-24 h-24 rounded-full border-2 border-[#D4A853]/25 absolute top-4 left-4" data-id="element-218" />
        <div className="w-16 h-16 rounded-full border border-[#E8C97A]/30 absolute top-8 left-8" data-id="element-219" />
      </div>
      <div className="lg:hidden absolute bottom-16 right-1/4 flex gap-3" data-id="element-220">
        <div className="w-3 h-3 rounded-full bg-[#E8C97A]/35" data-id="element-221" />
        <div className="w-4 h-4 rounded-full bg-[#D4A853]/30" data-id="element-222" />
        <div className="w-3 h-3 rounded-full bg-[#E8C97A]/35" data-id="element-223" />
        <div className="w-2.5 h-2.5 rounded-full bg-[#D4A853]/30" data-id="element-224" />
      </div>

      {/* Additional decorative elements for more visibility */}
      {/* Large decorative swirl - left side */}
      <div className="absolute top-1/2 -translate-y-1/2 left-0 sm:left-4" data-id="element-225">
        <div className="w-16 h-32 sm:w-20 sm:h-40 border-l-2 border-t-2 border-[#E8C97A]/25 rounded-tl-[100px]" data-id="element-226" />
      </div>

      {/* Scattered pearls - top area */}
      <div className="absolute top-32 left-1/3 hidden sm:block" data-id="element-227">
        <div className="w-3 h-3 rounded-full bg-[#D4A853]/30" data-id="element-228" />
      </div>
      <div className="absolute top-40 left-[40%] hidden sm:block" data-id="element-229">
        <div className="w-2.5 h-2.5 rounded-full bg-[#E8C97A]/35" data-id="element-230" />
      </div>

      {/* Gradient overlay for depth */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#5C3D2E]/75 via-transparent to-[#4A3225]/50" data-id="element-231" />

      {/* Main container */}
      <div className="relative flex flex-col lg:flex-row items-center min-h-[60vh] sm:min-h-[70vh] lg:min-h-[85vh] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-232">
        {/* Left side - Content */}
        <div className="relative w-full lg:w-[45%] z-10 py-12 sm:py-16 lg:py-20" data-id="element-233">
          <span className="inline-flex items-center gap-2 text-[#D4A853] tracking-[0.12em] sm:tracking-[0.18em] uppercase text-sm sm:text-base font-medium mb-6" data-id="element-234">
            <span className="w-6 sm:w-8 h-px bg-[#D4A853]" data-id="element-235" />
            For Africans, By an African
            <span className="w-6 sm:w-8 h-px bg-[#D4A853]" data-id="element-236" />
          </span>

          <h1 className="text-7xl sm:text-6xl md:text-6xl lg:text-7xl font-['Playfair_Display'] text-white leading-[1.1] mb-6" data-id="element-237">
            Elegance
            <br data-id="element-238" />
            Rooted in
            <br data-id="element-239" />
            <span className="italic text-[#D4A853]" data-id="element-240">Heritage</span>
          </h1>

          <p className="text-2xl sm:text-3xl text-white/75 font-['Raleway'] max-w-md leading-relaxed mb-8" data-id="element-241">
            Celebrating African beauty with curated pieces that honor our rich
            traditions while embracing modern sophistication.
          </p>

          <div className="flex flex-wrap gap-3 sm:gap-4" data-id="element-242">
            <Button size="lg" variant="secondary" className="h-12 sm:h-14 px-6 sm:px-10 text-xs sm:text-sm bg-[#D4A853] text-[#1A1A1A] hover:bg-[#E8C97A] uppercase tracking-wider font-medium" onClick={() => navigate('shop')} data-id="element-243">
              Shop Collection
            </Button>
            <Button size="lg" variant="outline" className="h-12 sm:h-14 px-6 sm:px-10 text-xs sm:text-sm border-white/40 text-white hover:bg-white/10 uppercase tracking-wider font-medium" onClick={() => navigate('about')} data-id="element-244">
              Our Story
            </Button>
          </div>
        </div>

        {/* Right side - Circular Image Slider (desktop only) */}
        <div className="hidden lg:flex relative w-[55%] items-center justify-center py-8 lg:py-0 -mt-[33px]" data-id="element-245">
          {/* Circular frame container */}
          <div className="relative" data-id="element-246">
            {/* Decorative rings - fashion-inspired */}
            <div className="absolute -inset-6 xl:-inset-8 rounded-full border-2 border-[#E8C97A]/40" data-id="element-247" />
            <div className="absolute -inset-12 xl:-inset-16 rounded-full border border-[#D4A853]/25 border-dashed" data-id="element-248" />
            <div className="absolute -inset-[4.5rem] xl:-inset-24 rounded-full border border-[#E8C97A]/12" data-id="element-249" />

            {/* Main circular slider */}
            <div className="relative w-[500px] h-[500px] xl:w-[580px] xl:h-[580px] 2xl:w-[650px] 2xl:h-[650px] rounded-full overflow-hidden shadow-2xl" data-id="element-250">
              {/* Image slider */}
              {slides.map((slide, index) => <div key={slide.id} className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`} data-id="element-251">
                  <img src={slide.image} alt={slide.alt} className="w-full h-full object-cover object-top" data-id="element-252" />
                </div>)}

              {/* Inner shadow for depth */}
              <div className="absolute inset-0 rounded-full shadow-[inset_0_0_50px_rgba(0,0,0,0.15)]" data-id="element-253" />
            </div>

            {/* Navigation arrows */}
            <button onClick={goToPrev} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-14 h-14 xl:w-16 xl:h-16 rounded-full bg-white/85 backdrop-blur-sm shadow-xl flex items-center justify-center text-[#5C3D2E] hover:bg-[#D4A853] hover:text-white transition-all z-10" aria-label="Previous slide" data-id="element-254">
              <ChevronLeft size={24} strokeWidth={2} data-id="element-255" />
            </button>
            <button onClick={goToNext} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-14 h-14 xl:w-16 xl:h-16 rounded-full bg-white/85 backdrop-blur-sm shadow-xl flex items-center justify-center text-[#5C3D2E] hover:bg-[#D4A853] hover:text-white transition-all z-10" aria-label="Next slide" data-id="element-256">
              <ChevronRight size={24} strokeWidth={2} data-id="element-257" />
            </button>

            {/* Slide indicators */}
            <div className="absolute -bottom-14 left-1/2 -translate-x-1/2 flex items-center gap-3" data-id="element-258">
              {slides.map((_, index) => <button key={index} onClick={() => goToSlide(index)} className={`h-3 rounded-full transition-all duration-300 ${index === currentSlide ? 'bg-[#D4A853] w-8' : 'bg-white/50 w-3 hover:bg-white/70'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-259" />)}
            </div>
          </div>
        </div>
      </div>

      {/* Kente-inspired bottom accent */}
      <div className="absolute bottom-0 left-0 right-0 h-1.5 kente-stripe z-20" data-id="element-260" />
    </section>;
}